import { useState } from 'react'
import { CreditCard, Pencil, Plus, Trash2 } from 'lucide-react'
import { ConfirmDialog, EmptyState, ErrorState, FormDialog, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePermission } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import { useBankAccountMutations, useBankAccounts } from '../api'
import type { BankAccountView } from '../types'
import { TextField } from './EmployeeFields'

const EMPTY = {
  bankName: '', branch: '', accountHolderName: '', accountNumber: '', iban: '',
  routingCode: '', swiftCode: '', currency: '', primaryFlag: false,
}

/**
 * Where somebody's salary is paid.
 *
 * <p>The table has existed since the person/assignment split and had no endpoint and no
 * screen, so the only bank account that ever reached the database came from the demo seed.
 *
 * <p>Two rules the screen makes visible rather than enforcing quietly: exactly one account is
 * primary, and removing one is a deactivation. The refusal when somebody tries to deactivate
 * the account salary is actually paid into is the server's, shown as it was written — a person
 * left with accounts and no primary is one a payment file skips without failing anywhere.
 */
export function BankAccountsTab({ employeeId }: { employeeId: string }) {
  const accounts = useBankAccounts(employeeId)
  const { save, deactivate } = useBankAccountMutations(employeeId)
  const canWrite = usePermission('employee:write')

  const [draft, setDraft] = useState<(typeof EMPTY & { id?: string }) | null>(null)
  const [removing, setRemoving] = useState<BankAccountView | null>(null)
  const [error, setError] = useState<string | null>(null)

  function open(account?: BankAccountView) {
    setError(null)
    setDraft(account
      ? {
          id: account.id,
          bankName: account.bankName,
          branch: account.branch ?? '',
          accountHolderName: account.accountHolderName ?? '',
          // Never sent back: the server holds it encrypted and returns only the last four,
          // so echoing the mask into the field would save "••••1234" as the account number.
          accountNumber: '',
          iban: account.iban ?? '',
          routingCode: account.routingCode ?? '',
          swiftCode: account.swiftCode ?? '',
          currency: account.currency ?? '',
          primaryFlag: account.primaryFlag,
        }
      : { ...EMPTY })
  }

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await save.mutateAsync({
        ...draft,
        branch: draft.branch || null,
        accountHolderName: draft.accountHolderName || null,
        accountNumber: draft.accountNumber || null,
        iban: draft.iban || null,
        routingCode: draft.routingCode || null,
        swiftCode: draft.swiftCode || null,
        currency: draft.currency || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That account could not be saved.')
    }
  }

  async function remove() {
    if (!removing) return
    setError(null)
    try {
      await deactivate.mutateAsync(removing.id)
      setRemoving(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That account could not be deactivated.')
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <CreditCard className="size-4" aria-hidden="true" />
          Bank accounts
        </h3>
        {canWrite && (
          <Button type="button" size="sm" onClick={() => open()}>
            <Plus className="size-4" aria-hidden="true" />
            Add account
          </Button>
        )}
      </div>

      {accounts.isPending && <Skeleton className="h-24" />}
      {accounts.isError && (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      )}
      {accounts.isSuccess && accounts.data.length === 0 && (
        <EmptyState
          title="No bank account on file"
          description="Payroll cannot pay somebody it has no account for. Add one when the details arrive."
        />
      )}

      {accounts.isSuccess && accounts.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-start text-xs text-[var(--color-muted)]">
              <tr>
                <th className="p-2 text-start font-medium">Bank</th>
                <th className="p-2 text-start font-medium">IBAN</th>
                <th className="p-2 text-start font-medium">Account</th>
                <th className="p-2 text-start font-medium">Currency</th>
                <th className="p-2 text-start font-medium">Primary</th>
                {canWrite && <th className="p-2 text-end font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {accounts.data.map((account) => (
                <tr key={account.id} className="border-t border-[var(--color-border)]">
                  <td className="p-2">
                    <span className="text-[var(--color-heading)]">{account.bankName}</span>
                    {account.branch && (
                      <span className="ms-2 text-xs text-[var(--color-muted)]">
                        {account.branch}
                      </span>
                    )}
                  </td>
                  <td className="p-2 font-mono text-xs">{account.iban ?? '—'}</td>
                  <td className="p-2 font-mono text-xs">{account.accountNumberMasked ?? '—'}</td>
                  <td className="p-2">{account.currency ?? '—'}</td>
                  <td className="p-2">
                    {account.primaryFlag
                      ? <StatusBadge status="PRIMARY" tone="success" label="Salary paid here" />
                      : <span className="text-[var(--color-muted)]">—</span>}
                  </td>
                  {canWrite && (
                    <td className="p-2 text-end">
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Edit ${account.bankName}`}
                        onClick={() => open(account)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Deactivate ${account.bankName}`}
                        onClick={() => { setError(null); setRemoving(account) }}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && !draft && !removing && (
        <p role="alert" className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>
      )}

      <FormDialog
        open={draft !== null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title={draft?.id ? 'Edit bank account' : 'Add bank account'}
        description="An IBAN or an account number — one of the two is enough to be paid."
        size="lg"
        submitLabel="Save account"
        loading={save.isPending}
        error={error}
        onSubmit={() => { void submit() }}
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField id="account-bank" label="Bank" required
              value={draft.bankName} onChange={(v) => setDraft({ ...draft, bankName: v })} />
            <TextField id="account-branch" label="Branch"
              value={draft.branch} onChange={(v) => setDraft({ ...draft, branch: v })} />
            <TextField id="account-holder-name" label="Account holder"
              hint="If it differs from the employee's own name."
              value={draft.accountHolderName}
              onChange={(v) => setDraft({ ...draft, accountHolderName: v })} />
            <TextField id="account-iban" label="IBAN"
              value={draft.iban} onChange={(v) => setDraft({ ...draft, iban: v })} />
            <TextField id="account-number-field" label="Account number"
              hint={draft.id
                ? 'Blank leaves the stored number alone — it is encrypted and never sent back.'
                : undefined}
              value={draft.accountNumber}
              onChange={(v) => setDraft({ ...draft, accountNumber: v })} />
            <TextField id="account-swift" label="SWIFT / BIC"
              value={draft.swiftCode} onChange={(v) => setDraft({ ...draft, swiftCode: v })} />
            <TextField id="account-routing" label="Routing code"
              value={draft.routingCode} onChange={(v) => setDraft({ ...draft, routingCode: v })} />
            <TextField id="account-currency" label="Currency"
              value={draft.currency} onChange={(v) => setDraft({ ...draft, currency: v })} />
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.primaryFlag}
                onChange={(event) => setDraft({ ...draft, primaryFlag: event.target.checked })}
              />
              Pay salary into this account. Making this one primary demotes whichever account
              is primary now — there is only ever one.
            </label>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => { if (!open) setRemoving(null) }}
        title={`Deactivate ${removing?.bankName ?? 'this account'}?`}
        description="It stops being offered for payment and stays on file, so a payment already made still resolves the account it named. It is never deleted."
        confirmLabel="Deactivate"
        loading={deactivate.isPending}
        error={error}
        onConfirm={() => { void remove() }}
      />
    </Card>
  )
}
